import type {
  SellToRestrictionCapabilitiesResponse,
  SellToRestrictionDecisionsResponse,
  SellToRestrictionPolicyRequest,
  SellToRestrictionsResponse,
  ShopSellToRestriction,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Sell-to restrictions: the buyer countries a merchant refuses to sell to,
 * merchant-wide and per shop. A refused payment fails with error code `DE_10`.
 *
 * Every route needs a dashboard session. Reading needs the `SellToRestrictions`
 * area at `Read`, overriding a shop needs `Edit` (a shop-scoped role may
 * override only its own shop), and changing the merchant-wide policy needs
 * `Write`.
 */
export class SellToRestrictions {
  constructor(private readonly request: RequestFn) {}

  /**
   * The merchant-wide policy and every shop's effective policy. A caller
   * scoped to one shop receives that shop alone, and no merchant-wide policy.
   * `GET /sell-to-restrictions`
   */
  async retrieve(): Promise<SellToRestrictionsResponse> {
    return this.request('GET', '/sell-to-restrictions');
  }

  /**
   * Replace the merchant-wide policy every shop without an override inherits.
   * The change is recorded in the audit log before it is made.
   * `PUT /sell-to-restrictions/merchant`
   */
  async updateMerchantPolicy(
    params: SellToRestrictionPolicyRequest,
  ): Promise<SellToRestrictionsResponse> {
    return this.request('PUT', '/sell-to-restrictions/merchant', { body: params });
  }

  /**
   * Give one shop its own policy, replacing the merchant-wide policy for that
   * shop. An empty `blocked_countries` lifts every restriction for it.
   * `PUT /sell-to-restrictions/shops/{profile_id}`
   */
  async updateShopOverride(
    profileId: string,
    params: SellToRestrictionPolicyRequest,
  ): Promise<ShopSellToRestriction> {
    return this.request('PUT', `/sell-to-restrictions/shops/${encodeURIComponent(profileId)}`, {
      body: params,
    });
  }

  /**
   * Remove one shop's override, so it inherits the merchant-wide policy again.
   * Removing an override the shop does not have changes nothing.
   * `DELETE /sell-to-restrictions/shops/{profile_id}`
   */
  async deleteShopOverride(profileId: string): Promise<ShopSellToRestriction> {
    return this.request('DELETE', `/sell-to-restrictions/shops/${encodeURIComponent(profileId)}`);
  }

  /**
   * Which checks each payment path performs, and how this deployment resolves
   * a buyer's country — read before relying on a restriction for a path.
   * `GET /sell-to-restrictions/capabilities`
   */
  async capabilities(): Promise<SellToRestrictionCapabilitiesResponse> {
    return this.request('GET', '/sell-to-restrictions/capabilities');
  }

  /**
   * Why a payment was refused: where, by which signal, for which country and
   * under which policy. A shop-scoped caller sees only refusals in its shop.
   * `GET /sell-to-restrictions/payments/{payment_id}/decisions`
   */
  async listDecisions(paymentId: string): Promise<SellToRestrictionDecisionsResponse> {
    return this.request(
      'GET',
      `/sell-to-restrictions/payments/${encodeURIComponent(paymentId)}/decisions`,
    );
  }
}
